/**
 * Integrytas Group - Common Security Module (CSM)
 * api_client - the browser-side handle to the csm-api Edge Function gateway.
 *
 * This is the "API-first" seam: the UI never touches PostgREST directly any
 * more. `apiDb` exposes a tiny PostgREST-shaped builder so existing call sites
 * migrate almost mechanically (`supabase.from(...)` -> `apiDb.from(...)`), but
 * every read and write is now a single JSON round-trip to the gateway, which
 * re-applies the operation server-side under the caller's own JWT (RLS intact).
 *
 * Supported chain (the subset the app actually uses):
 *   .from(table)
 *     .select(cols) | .insert(rows) | .update(values)
 *     | .upsert(rows, { onConflict }) | .delete()
 *   filters: .eq .neq .in .is .lt .lte .gt .gte .like .ilike .or .match
 *   .order(col, { ascending, nullsFirst }) .limit(n) .range(a, b)
 *   .select(cols)   // returning columns after a write
 *   .single() | .maybeSingle()
 *   await -> { data, error }
 *
 * Auth/session lifecycle stays on supabase-js (GoTrue is already an HTTP API);
 * we only borrow the access token to authenticate gateway calls.
 */

import { supabase } from './supabase_client';
import { asfContextHeader, asfWorkspaceCode } from './asf';
import { getDdmSchema } from './ddm_mode';

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
const GATEWAY_URL       = `${SUPABASE_URL}/functions/v1/csm-api`;

/**
 * PostgREST caps every result set at `max_rows` (supabase config.toml [api];
 * default 1000). A multi-row select that comes back with EXACTLY this many rows
 * and no explicit `.limit()`/`.range()` was almost certainly truncated — the
 * screen is silently showing only the first window of a larger table, and any
 * client-side search over `data` can't see the rest. This is the "load-all +
 * filter client-side" trap that hides records once a table grows past the cap.
 *
 * We surface it as a dev-only console.warn (naming the table) so at-risk screens
 * are caught during development/QA instead of via a customer "record missing"
 * report. The fix for a flagged screen is server-side filtering/pagination
 * (`.or(...ilike...)` + `.range()`/`.limit()`), never raising max_rows. Override
 * the threshold with VITE_LIST_CAP if the deployment's max_rows differs.
 */
const LIST_TRUNCATION_CAP = Number(import.meta.env.VITE_LIST_CAP ?? 1000);

/**
 * Mirrors supabase-js's loose runtime contract: callers always guard reads
 * with `data ?? []` or a prior `if (error) throw`, so `data` is typed
 * permissively (default `any`) to avoid strict-null friction. `.single<T>()`
 * narrows it to a concrete non-null row type.
 */
export interface ApiResult<T = any> {
  data: T;
  error: { message: string; code?: string } | null;
  count?: number | null;
}

type CountMode = 'exact' | 'planned' | 'estimated';

type FilterOp =
  | 'eq' | 'neq' | 'in' | 'is' | 'lt' | 'lte' | 'gt' | 'gte'
  | 'like' | 'ilike' | 'or';

interface Filter { col: string; op: FilterOp; value: unknown }
interface Order  { col: string; ascending?: boolean; nullsFirst?: boolean }
type Returns = 'many' | 'single' | 'maybeSingle';
type Verb = 'select' | 'insert' | 'update' | 'upsert' | 'delete';

interface WireRequest {
  kind: string;
  table?: string;
  columns?: string;
  filters?: Filter[];
  order?: Order[];
  limit?: number;
  range?: [number, number];
  returns?: Returns;
  count?: CountMode;
  head?: boolean;
  rows?: unknown;
  values?: Record<string, unknown>;
  onConflict?: string;
  ignoreDuplicates?: boolean;
  select?: string | boolean;
  name?: string;
  args?: Record<string, unknown>;
  payload?: unknown;
  bucket?: string;
  path?: string;
  paths?: string[];
  options?: Record<string, unknown>;
  fdm?: { acknowledged?: boolean; override_password?: string; reason?: string };
  strong?: boolean;
}

/** A fresh idempotency key for a mutating request (network-retry de-dup). */
function newIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `ike-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function gatewayFetch(body: WireRequest, idempotencyKey?: string): Promise<ApiResult> {
  let token = SUPABASE_ANON_KEY;
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) token = data.session.access_token;
  } catch { /* fall back to anon */ }

  try {
    const resp = await fetch(GATEWAY_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`,
        'x-csm-client': 'csm-web',
        'x-asf-context': asfContextHeader(),
        'x-ddm-schema': getDdmSchema(),
        ...(asfWorkspaceCode() ? { 'x-gtc-workspace': asfWorkspaceCode() } : {}),
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      },
      body: JSON.stringify(body),
    });
    const json = await resp.json().catch(() => ({ data: null, error: `CSM.API.BAD_RESPONSE (${resp.status})` }));
    const errMsg: string | null = json.error ?? (resp.ok ? null : `CSM.API.HTTP_${resp.status}`);
    return {
      data: json.data ?? null,
      error: errMsg ? { message: errMsg, code: json.code ?? undefined } : null,
      count: json.count ?? null,
    };
  } catch (e) {
    const raw = (e as Error).message ?? '';
    // Browser network errors carry browser-specific messages ("Load failed" on Safari,
    // "Failed to fetch" on Chrome/Edge, "NetworkError…" on Firefox) that are meaningless
    // to end users. Normalise them to a single actionable message so every derived app
    // shows a consistent, diagnosable error instead of a raw browser string.
    const isNetworkError =
      e instanceof TypeError &&
      (raw === 'Load failed' ||
       raw === 'Failed to fetch' ||
       raw.startsWith('NetworkError') ||
       raw.toLowerCase().includes('network connection was lost'));
    return {
      data: null,
      error: {
        message: isNetworkError
          ? 'Cannot connect to the application server. Please ensure the backend is running and try again.'
          : raw,
        code: isNetworkError ? 'CSM.API.NETWORK_UNREACHABLE' : undefined,
      },
      count: null,
    };
  }
}

/**
 * PostgREST-shaped, thenable query builder. Methods mutate internal state and
 * return `this`; awaiting it (or calling `.then`) fires exactly one gateway
 * request. The resolved promise is cached so accidental double-await is safe.
 */
class QueryBuilder<T = any> implements PromiseLike<ApiResult<T>> {
  private table: string;
  private verb: Verb | null = null;
  private columns = '*';
  private rows: unknown;
  private values?: Record<string, unknown>;
  private onConflict?: string;
  private ignoreDuplicates?: boolean;
  private filters: Filter[] = [];
  private orders: Order[] = [];
  private limitN?: number;
  private rangeAB?: [number, number];
  private returns: Returns = 'many';
  private returningSelect?: string | boolean;
  private countMode?: CountMode;
  private headOnly?: boolean;
  private dataTier: 'hot' | 'unified' | 'cold' = 'hot';
  private flatTarget = false;
  private strongRead = false;
  private fdmOpts?: { acknowledged?: boolean; override_password?: string; reason?: string };
  private idemKey?: string;
  private exec?: Promise<ApiResult<T>>;

  constructor(table: string) { this.table = table; }

  select(columns = '*', opts?: { count?: CountMode; head?: boolean }): this {
    if (this.verb === null) {
      this.verb = 'select';
      this.columns = columns;
      if (opts?.count) this.countMode = opts.count;
      if (opts?.head)  this.headOnly  = opts.head;
    } else {
      // .select() after a write -> returning columns
      this.returningSelect = columns;
    }
    return this;
  }

  insert(rows: unknown): this { this.verb = 'insert'; this.rows = rows; return this; }
  update(values: Record<string, unknown>): this { this.verb = 'update'; this.values = values; return this; }
  upsert(rows: unknown, opts?: { onConflict?: string; ignoreDuplicates?: boolean }): this {
    this.verb = 'upsert'; this.rows = rows;
    this.onConflict = opts?.onConflict;
    this.ignoreDuplicates = opts?.ignoreDuplicates;
    return this;
  }
  delete(): this { this.verb = 'delete'; return this; }

  /**
   * FDM breaking-change acknowledgment for a guarded delete/deprecate. Pass
   * { acknowledged: true, override_password } to force a cascade the dependency
   * guard would otherwise block (the override is verified server-side).
   */
  fdm(opts: { acknowledged?: boolean; override_password?: string; reason?: string }): this { this.fdmOpts = opts; return this; }

  /**
   * Override the auto-generated Idempotency-Key. Supply a STABLE key across
   * retries of one logical operation so the gateway de-duplicates them; the
   * default is a fresh per-call UUID (satisfies the mandatory-key gate).
   */
  idempotencyKey(key: string): this { this.idemKey = key; return this; }

  private addFilter(col: string, op: FilterOp, value: unknown): this {
    this.filters.push({ col, op, value }); return this;
  }
  eq(col: string, value: unknown): this    { return this.addFilter(col, 'eq', value); }
  neq(col: string, value: unknown): this   { return this.addFilter(col, 'neq', value); }
  in(col: string, value: unknown[]): this  { return this.addFilter(col, 'in', value); }
  is(col: string, value: unknown): this    { return this.addFilter(col, 'is', value); }
  lt(col: string, value: unknown): this    { return this.addFilter(col, 'lt', value); }
  lte(col: string, value: unknown): this   { return this.addFilter(col, 'lte', value); }
  gt(col: string, value: unknown): this    { return this.addFilter(col, 'gt', value); }
  gte(col: string, value: unknown): this   { return this.addFilter(col, 'gte', value); }
  like(col: string, value: string): this   { return this.addFilter(col, 'like', value); }
  ilike(col: string, value: string): this  { return this.addFilter(col, 'ilike', value); }
  or(expr: string): this                   { return this.addFilter('', 'or', expr); }
  match(obj: Record<string, unknown>): this {
    for (const [k, v] of Object.entries(obj)) this.addFilter(k, 'eq', v);
    return this;
  }

  order(col: string, opts?: { ascending?: boolean; nullsFirst?: boolean }): this {
    this.orders.push({ col, ascending: opts?.ascending ?? true, nullsFirst: opts?.nullsFirst });
    return this;
  }
  limit(n: number): this { this.limitN = n; return this; }
  range(a: number, b: number): this { this.rangeAB = [a, b]; return this; }

  /**
   * Dynamic Data View execution strategy (runtime context parameter):
   *   'hot'     - primary tier only (default; reads the base table)
   *   'unified' - hot + cold, via the security_invoker <table>_unified view
   *   'cold'    - archive tier only (the unified view filtered to data_tier='COLD')
   * RLS is enforced identically on both legs because the unified view runs
   * security_invoker and the cold mirror carries copied policies. Select only.
   */
  tier(t: 'hot' | 'unified' | 'cold'): this { this.dataTier = t; return this; }

  /**
   * Target the DSE flattened view (dse_<table>_flat) so user-defined columns are
   * selectable/filterable/sortable as native typed columns. Select only — e.g.
   * apiDb.from('po_purchase_order').flat().select('*').gt('custom_date', today).
   */
  flat(on = true): this { this.flatTarget = on; return this; }

  /**
   * ROR: force the primary tier for this read (strong consistency). Use for
   * read-after-write / form-confirmation reads that must see the just-written row.
   */
  strong(on = true): this { this.strongRead = on; return this; }

  single<U = T>(): QueryBuilder<U>      { this.returns = 'single'; return this as unknown as QueryBuilder<U>; }
  maybeSingle<U = T>(): QueryBuilder<U> { this.returns = 'maybeSingle'; return this as unknown as QueryBuilder<U>; }

  private toWire(): WireRequest {
    const verb = this.verb ?? 'select';
    // Flat strategy targets the DSE security_invoker view so user-defined columns
    // project as real typed columns (filter/sort/format like 3NF columns). Tier
    // strategy rewrites the relation to the unified view. Both are select-only.
    const effTable = (verb === 'select' && this.flatTarget)
      ? `dse_${this.table}_flat`
      : (verb === 'select' && this.dataTier !== 'hot')
        ? `${this.table}_unified`
        : this.table;
    const base: WireRequest = { kind: verb, table: effTable };
    if (verb === 'select') {
      base.columns = this.columns;
      base.filters = this.dataTier === 'cold'
        ? [...this.filters, { col: 'data_tier', op: 'eq', value: 'COLD' }]
        : this.filters;
      base.order   = this.orders;
      base.returns = this.returns;
      if (this.countMode) base.count = this.countMode;
      if (this.headOnly)  base.head  = true;
      if (this.limitN !== undefined) base.limit = this.limitN;
      if (this.rangeAB) base.range = this.rangeAB;
      if (this.strongRead) base.strong = true;
      return base;
    }
    // write verbs
    base.returns = this.returns;
    if (this.returningSelect !== undefined) base.select = this.returningSelect;
    if (verb === 'insert') { base.rows = this.rows; }
    else if (verb === 'upsert') {
      base.rows = this.rows;
      if (this.onConflict) base.onConflict = this.onConflict;
      if (this.ignoreDuplicates !== undefined) base.ignoreDuplicates = this.ignoreDuplicates;
    }
    else if (verb === 'update') { base.values = this.values; base.filters = this.filters; }
    else if (verb === 'delete') { base.filters = this.filters; }
    if (this.fdmOpts && (verb === 'delete' || verb === 'update')) base.fdm = this.fdmOpts;
    return base;
  }

  /**
   * Dev-only guard: warn when a multi-row select comes back at exactly the
   * PostgREST cap with no explicit bound — a strong signal the result was
   * truncated and the screen is showing a partial window. See LIST_TRUNCATION_CAP.
   */
  private warnIfTruncated(res: ApiResult<T>): void {
    if (!import.meta.env.DEV) return;
    const isSelect = this.verb === 'select' || this.verb === null;
    const bounded  = this.limitN !== undefined || this.rangeAB !== undefined;
    if (!isSelect || this.returns !== 'many' || bounded || this.headOnly) return;
    const rows = (res as ApiResult<unknown>)?.data;
    if (Array.isArray(rows) && rows.length === LIST_TRUNCATION_CAP) {
      console.warn(
        `[apiDb] '${this.table}' select returned exactly ${LIST_TRUNCATION_CAP} rows — likely ` +
        `truncated at the PostgREST row cap. This list is showing only the first ${LIST_TRUNCATION_CAP} ` +
        `rows and any client-side search can't see the rest. Add server-side filtering/pagination ` +
        `(.or(...ilike...) + .range()/.limit()) instead of loading all rows and filtering client-side.`,
      );
    }
  }

  private run(): Promise<ApiResult<T>> {
    if (!this.exec) {
      // Mutating verbs require an Idempotency-Key (gateway enforces); auto-generate
      // one per call so existing call sites work unchanged. Reads carry none.
      const mutating = this.verb === 'insert' || this.verb === 'update' || this.verb === 'upsert' || this.verb === 'delete';
      const key = this.idemKey ?? (mutating ? newIdempotencyKey() : undefined);
      const exec = gatewayFetch(this.toWire(), key) as Promise<ApiResult<T>>;
      this.exec = mutating
        ? exec
        : exec.then(res => { this.warnIfTruncated(res); return res; });
    }
    return this.exec;
  }

  then<R1 = ApiResult<T>, R2 = never>(
    onfulfilled?: ((value: ApiResult<T>) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: unknown) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    return this.run().then(onfulfilled, onrejected);
  }
}

// ---------------------------------------------------------------------------
// Storage - thin shim over the gateway + signed-URL upload.
// getPublicUrl is a pure string build (matches supabase-js, stays synchronous).
// ---------------------------------------------------------------------------

class StorageBucket {
  constructor(private bucket: string) {}

  getPublicUrl(path: string): { data: { publicUrl: string } } {
    return { data: { publicUrl: `${SUPABASE_URL}/storage/v1/object/public/${this.bucket}/${path}` } };
  }

  async remove(paths: string[]): Promise<ApiResult> {
    return gatewayFetch({ kind: 'storage_remove', bucket: this.bucket, paths });
  }

  async list(path = '', options?: Record<string, unknown>): Promise<ApiResult> {
    return gatewayFetch({ kind: 'storage_list', bucket: this.bucket, path, options });
  }

  /** Time-limited signed URL for downloading/viewing an object in a private bucket. */
  async createSignedUrl(path: string, expiresIn = 300): Promise<ApiResult> {
    return gatewayFetch({ kind: 'storage_create_signed_url', bucket: this.bucket, path, options: { expiresIn } });
  }

  /**
   * Upload via a gateway-issued signed URL. The DB-authorization decision is
   * made server-side (createSignedUploadUrl runs under the caller's JWT); the
   * bytes are then PUT straight to Storage with the one-time token.
   */
  async upload(
    path: string, file: File | Blob,
    opts?: { contentType?: string; upsert?: boolean; cacheControl?: string },
  ): Promise<ApiResult> {
    const signed = await gatewayFetch({ kind: 'storage_sign_upload', bucket: this.bucket, path });
    if (signed.error || !signed.data) return { data: null, error: signed.error ?? { message: 'CSM.API.SIGN_FAILED' } };
    const token = (signed.data as { token: string }).token;
    const { error } = await supabase.storage
      .from(this.bucket)
      .uploadToSignedUrl(path, token, file, opts);
    return { data: error ? null : { path }, error: error ? { message: error.message } : null };
  }
}

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

export const apiDb = {
  from<T = any>(table: string): QueryBuilder<T> { return new QueryBuilder<T>(table); },

  rpc<T = any>(name: string, args?: Record<string, unknown>, opts?: { idempotencyKey?: string }): PromiseLike<ApiResult<T>> {
    // The Common Routine is the funnelled write path -> mandatory Idempotency-Key
    // (gateway-enforced). Auto-generate one unless the caller supplies a stable key.
    const key = opts?.idempotencyKey ?? (name === 'csm_common_routine_with_context' ? newIdempotencyKey() : undefined);
    return {
      then: (onf, onr) => gatewayFetch({ kind: 'rpc', name, args }, key).then(onf as any, onr),
    } as PromiseLike<ApiResult<T>>;
  },

  storage: {
    from(bucket: string): StorageBucket { return new StorageBucket(bucket); },
  },

  authCreateUser(params: {
    email: string;
    password: string;
    display_name?: string;
    preferred_locale?: string;
  }): PromiseLike<ApiResult<{ id: string }>> {
    return {
      then: (onf, onr) => gatewayFetch({ kind: 'auth_create_user', args: params }).then(onf as any, onr),
    } as PromiseLike<ApiResult<{ id: string }>>;
  },

  /**
   * Enable or disable a user's Supabase (GoTrue) login, admin-only, WITHOUT
   * touching their csm_user_master business record. Enable creates the account
   * (admin-set password, min 8 chars) when none exists or lifts a ban; disable
   * bans the account (reversible). A user can therefore exist with no login.
   */
  authSetLogin(params: {
    email: string;
    enabled: boolean;
    password?: string;
  }): PromiseLike<ApiResult<{ ok: boolean }>> {
    return {
      then: (onf, onr) => gatewayFetch({ kind: 'auth_set_login', args: params }).then(onf as any, onr),
    } as PromiseLike<ApiResult<{ ok: boolean }>>;
  },

  /**
   * DEV-ONLY quick login. Asks the gateway to mint a one-time OTP for the first
   * super-admin (gated server-side by CSM.SECURITY.DEV_LOGIN_ENABLED). Returns
   * the admin email + OTP, which the caller verifies into a real session.
   */
  devLogin(): PromiseLike<ApiResult<{ email: string; otp: string }>> {
    return {
      then: (onf, onr) => gatewayFetch({ kind: 'dev_login' }).then(onf as any, onr),
    } as PromiseLike<ApiResult<{ email: string; otp: string }>>;
  },

  /** LIC: the active tenant's verified module entitlement (read-only). */
  licStatus<T = any>(): PromiseLike<ApiResult<T>> {
    return {
      then: (onf, onr) => gatewayFetch({ kind: 'lic_status' }).then(onf as any, onr),
    } as PromiseLike<ApiResult<T>>;
  },
};

export type ApiDb = typeof apiDb;
